import { VisitorInside } from '../../interfaces/hierarchy-interfaces';

/* ───────────────────────────  ONE GHOST, ONE WORDING  ───────────────────────
 *
 * Two screens show visitors who are still inside — the Who's Home board
 * (dashboard-building-roster.tsx) and the sign-out screen
 * (visitor-exit-page.tsx). Both must describe a "ghost" — an arrival from a
 * previous local day that nobody ever closed — in EXACTLY the same words.
 *
 * They did not. Each grew its own formatter and the two had already drifted
 * before anyone noticed:
 *
 *   roster    days_open <= 1  -> "since yesterday"
 *   exit page days_open === 1 -> "open since yesterday",
 *                                so days_open === 0 fell through to the plural
 *                                branch and rendered "0 days open"
 *
 * A ghost is the one thing on these screens that must not be ambiguous: it is
 * NOT evidence that somebody is in the building, and it must never read like
 * somebody who walked in on this shift. Two wordings for that fact is how a
 * front desk ends up treating a three-week-old row as a person upstairs.
 *
 * So the wording lives here, once, and both screens import it. The boundary is
 * `<= 1` deliberately: days_open of 0 or 1 both mean "since yesterday" — a row
 * opened late last night and swept at the building's local midnight is one day
 * old at most, and "0 days open" is not a thing a human says.
 */

/** How long a ghost has been open. Never called for a non-stale row. */
export const staleAgeLabel = (visitor: VisitorInside): string =>
  visitor.days_open <= 1 ? 'open since yesterday' : `${visitor.days_open} days open`;

/**
 * The full ghost tag, or null when the row is a genuine current arrival.
 * `null` is meaningful: it is the caller's signal to render nothing at all
 * rather than an empty badge.
 */
export const describeStale = (row: VisitorInside): string | null =>
  row.stale ? `Never signed out · ${staleAgeLabel(row)}` : null;
